import React, { useEffect, useRef, useState, useContext } from "react";
import ReactDOM from "react-dom";
import { NewContext } from "../utils/ContextProvider";
import "../css/ModalResetPassword.css";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function ModalResetPassword({ show, onClose, initialEmail = "", className = "" }) {
    const { getAlert } = useContext(NewContext);
    const [email, setEmail] = useState(initialEmail);
    const [loading, setLoading] = useState(false);
    const [errorField, setErrorField] = useState("");
    const inputRef = useRef(null);

    const BASE_URL = process.env.REACT_APP_API_URL || "https://auth.softnik.tech/";
    const URL_SEND_EMAIL = `${BASE_URL.replace(/\/+$/, "")}/user/sendEmail`;

    useEffect(() => {
        if (show) {
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setEmail(initialEmail || "");
            setErrorField("");
            setLoading(false);
        }
    }, [show, initialEmail]);

    useEffect(() => {
        if (!show) return;
        const handler = (e) => {
            if (e.key === "Escape" && !loading) onClose?.();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [show, loading, onClose]);

    if (!show) return null;

    const sendResetEmailFetch = async (mail) => {
        const url = `${URL_SEND_EMAIL}?email=${encodeURIComponent(mail)}`;
        const resp = await fetch(url, {
            method: "POST",
            credentials: "omit",
            headers: {
                Accept: "*/*",
            },
            body: null,
            cache: "no-store",
            redirect: "follow",
        });

        let dataText = "";
        try {
            dataText = await resp.text();
        } catch {
        }

        let data = dataText;
        try {
            if (dataText && resp.headers.get("content-type")?.includes("application/json")) {
                data = JSON.parse(dataText);
            }
        } catch {
        }

        return { status: resp.status, data };
    };

    const handleSubmit = async () => {
        if (loading) return;

        if (!email || !emailRegex.test(email)) {
            setErrorField("Ingresá un correo válido.");
            return;
        }

        setLoading(true);
        try {
            const normalized = email.trim().toLowerCase();
            const resp = await sendResetEmailFetch(normalized);
            const status = resp?.status ?? 0;

            if (status === 200 || status === 204) {
                let msg = "📧 Email enviado con éxito.";

                if (typeof resp?.data === "string" && resp.data) {
                    if (resp.data.toLowerCase().includes("email has been sent")) {
                        msg = "📧 Email enviado con éxito, revise su casilla de correo";
                    } else {
                        msg = resp.data;
                    }
                }

                getAlert?.({ msj: msg, tipe: "succes" });
                onClose?.();
                return;
            }

            if ([400, 401, 403, 404].includes(status)) {
                const msg =
                    (resp?.data && (resp.data.message || resp.data.error || String(resp.data))) ||
                    "No se encontró un usuario con ese correo.";
                setErrorField(msg);
                getAlert?.({ msj: msg, tipe: "warning" });
            } else if (status >= 500) {
                const msg =
                    (resp?.data && (resp.data.message || resp.data.error || String(resp.data))) ||
                    "Ocurrió un error al enviar el correo.";
                setErrorField(msg);
                getAlert?.({ msj: msg, tipe: "error" });
            } else {
                const msg = "No pudimos enviar el correo. Intentá nuevamente.";
                setErrorField(msg);
                getAlert?.({ msj: msg, tipe: "warning" });
            }
        } catch (err) {
            const msg = "Ocurrió un error inesperado.";
            setErrorField(msg);
            getAlert?.({ msj: msg, tipe: "warning" });
        } finally {
            setLoading(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="modal-overlay-reset" aria-modal="true" role="dialog">
            <div
                className={`modal-reset ${className}`.trim()}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <div className="modal-reset-header">
                    <h3>Recuperar contraseña</h3>
                    <button
                        className="reset-close-btn"
                        type="button"
                        onClick={() => !loading && onClose?.()}
                        aria-label="Cerrar"
                    >
                        ×
                    </button>
                </div>

                <div className="modal-reset-body" role="form" aria-labelledby="resetEmail">
                    <p className="reset-help">
                        Ingresá el correo asociado a tu cuenta y te enviaremos un enlace para restablecer tu contraseña.
                    </p>

                    <div className="reset-row">
                        <label htmlFor="resetEmail">Correo electrónico</label>
                        <input
                            id="resetEmail"
                            ref={inputRef}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="tucorreo@empresa.com"
                            autoComplete="email"
                            disabled={loading}
                            aria-invalid={!!errorField}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSubmit();
                                }
                            }}
                        />
                        {errorField && <small className="reset-error">{errorField}</small>}
                    </div>

                    <div className="modal-reset-footer">
                        <button
                            className="reset-btn-primary"
                            type="button"
                            disabled={loading}
                            onClick={handleSubmit}
                        >
                            {loading ? "Enviando..." : "Enviar enlace"}
                        </button>
                        <button
                            className="reset-btn-cancel"
                            type="button"
                            onClick={() => !loading && onClose?.()}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}